import React, { useState, useEffect, useRef } from "react";
import ReactApexChart from "react-apexcharts";
import {
  Flex,
  Box,
  Header,
  Input,
  Table,
  Pagination,
  Button,
  Text,
} from "@fluentui/react-northstar";
import { SearchIcon } from "@fluentui/react-icons-northstar";
import PieChart from "../Charts/PieChart";
import { useTranslation } from "react-i18next";
/**
 * UserTab Component
 * Displays credit consumption data by user with search and pagination
 * @param {Object} props - Component props
 * @param {Object} props.data - User consumption data
 */
export const UserTab = ({ data, creditUsage, isLoading }) => {
  const { t } = useTranslation();

  const translation = t("aiConsumption").user;
  const commonTranslation = t("aiConsumption").common;

  if (isLoading) return <Text content={commonTranslation.loading} />;

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [paginatedUsers, setPaginatedUsers] = useState([]);
  const chartRef = useRef(null);
  const itemsPerPage = 5;

  // Chart configuration for user pie chart
  const [chartOptions, setChartOptions] = useState({
    chart: {
      type: "pie",
      height: 350,
    },
    labels: [],
    legend: {
      position: "right",
    },
    colors: [
      "#3b82f6", // blue
      "#10b981", // green
      "#f59e0b", // amber
      "#8b5cf6", // purple
      "#f43f5e", // pink
    ],
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 300,
          },
          legend: {
            position: "bottom",
          },
        },
      },
    ],
  });

  const [chartSeries, setChartSeries] = useState([]);

  // Calculate total credits for a user
  const calculateTotalCredits = (user) => {
    return (
      user.Whiteboard +
      user.Booking +
      user.Assignments +
      user.Events +
      user.Feedback
    );
  };

  // Initialize the chart when component mounts
  useEffect(() => {
    if (creditUsage) {
      // Get top 5 users by total usage
      const topUsers = [...creditUsage]
        .sort((a, b) => {
          const totalA = calculateTotalCredits(a);
          const totalB = calculateTotalCredits(b);
          return totalB - totalA;
        })
        .slice(0, 5);

      // Update chart data
      setChartOptions((prevOptions) => ({
        ...prevOptions,
        labels: topUsers.map((u) => u.name),
      }));

      setChartSeries(topUsers.map((u) => calculateTotalCredits(u)));
    }
  }, [creditUsage]);

  // Filter and paginate users when search term or page changes
  useEffect(() => {
    if (creditUsage) {
      // Filter users based on search term
      const filtered = creditUsage.filter((user) =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase())
      );

      setTotalUsers(filtered.length);

      // Paginate the filtered users
      const paginated = filtered.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      );

      setPaginatedUsers(paginated);
    }
  }, [creditUsage, searchTerm, currentPage]);

  // Handle search input change
  const handleSearchChange = (e, data) => {
    setSearchTerm(data.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  // Table header items
  const headerItems = [
    { key: "name", content: "User" },
    { key: "role", content: "Role" },
    { key: "whiteboard", content: "Whiteboard" },
    { key: "booking", content: "Booking" },
    { key: "assignments", content: "Assignments" },
    { key: "events", content: "Events" },
    { key: "feedback", content: "Feedback" },
    { key: "total", content: "Total" },
  ];

  // Generate table rows from paginated users data
  const tableRows = paginatedUsers.map((user) => {
    const total = calculateTotalCredits(user);

    return {
      key: user.id,
      items: [
        {
          content: user.name,
          key: `${user.id}-name`,
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "95%",
          },
        },
        {
          content: user.role,
          key: `${user.id}-role`,
          style: { textTransform: "capitalize" },
        },
        { content: user.Whiteboard, key: `${user.id}-whiteboard` },
        { content: user.Booking, key: `${user.id}-booking` },
        { content: user.Assignments, key: `${user.id}-assignments` },
        { content: user.Events, key: `${user.id}-events` },
        { content: user.Feedback, key: `${user.id}-feedback` },
        {
          content: total,
          key: `${user.id}-total`,
          style: { fontWeight: "bold" },
        },
      ],
    };
  });

  // Calculate total pages for pagination
  const totalPages = Math.ceil(totalUsers / itemsPerPage);

  return (
    <Flex column gap="gap.large">
      <Text
        size="large"
        weight="bold"
        content={translation.title}
        style={{
          color: "#1F2937",
        }}
      />

      <Flex gap="gap.large" wrap>
        {/* Chart Section */}
        <Flex column styles={{ flex: "1", minWidth: "300px" }} gap="gap.medium">
          <Text align="center" content={translation.chartTitle} />
          <PieChart options={chartOptions} data={chartSeries} />
        </Flex>

        {/* User Table Section */}
        <Box styles={{ flex: "2", minWidth: "650px" }}>
          {/* Search Input */}
          <Flex vAlign="center" styles={{ marginBottom: "1rem" }}>
            <Input
              fluid
              placeholder={translation.searchUsers}
              icon={<SearchIcon />}
              iconPosition="end"
              clearable
              value={searchTerm}
              onChange={handleSearchChange}
              styles={{ width: "100%" }}
            />
          </Flex>

          {/* Users Table */}
          <Table
            header={{ items: headerItems }}
            rows={tableRows}
            aria-label="User credit consumption table"
            styles={{ width: "100%", overflow: "auto" }}
          />

          {/* Pagination Section */}
          <Flex
            hAlign="space-between"
            vAlign="center"
            styles={{ marginTop: "1rem" }}
          >
            <Text size="small">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, totalUsers)} of {totalUsers}{" "}
              users
            </Text>

            <Flex gap="gap.small">
              <Button
                content="Previous"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                size="small"
              />
              <Button
                content="Next"
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, totalPages))
                }
                size="small"
              />
            </Flex>
          </Flex>
        </Box>
      </Flex>
    </Flex>
  );
};

export default UserTab;
